/**
 * Premium economy tuning. All prices are in Dark Matter — the premium
 * currency held on the server-owned `players/{uid}.premium.darkMatter`. Dark
 * Matter is obtained in-game today (starter grant + tutorial rewards);
 * buying it with real money is deferred to a separate payment issue, so
 * these values are the only thing gating each premium action.
 */
/** Dark Matter granted to a brand-new player when their first planet is created. */
export const STARTER_DARK_MATTER = 500;
/**
 * Sentinel expiry for a "permanent" premium tier — every tier carries an
 * `expiresAt`, so a non-expiring tier (e.g. a future subscription, or the
 * mock dev default) is just one dated far in the future (year 9999).
 */
export const PERMANENT_TIER_EXPIRES_AT = Date.UTC(9999, 0, 1);
/** Cost of one Emergency Ozone — instantly restores an ozone-locked planet to full Ozone. */
export const EMERGENCY_OZONE_PRICE = 100;
/** Cost to permanently unlock one premium planet image (ids above `FREE_PLANET_IMAGE_COUNT`). */
export const PREMIUM_PLANET_IMAGE_PRICE = 20;
/** Dark Matter cost per Market trade token bought via `/purchaseTradeTokens`. */
export const TRADE_TOKEN_PRICE = 10;
/** Dark Matter granted by a tutorial `premium` reward (on top of any tier boost it also grants). */
export const TUTORIAL_DARK_MATTER_REWARD = 100;
/**
 * Purchasable time-limited tier boosts, keyed by product id. Buying one calls
 * `grantPremiumTier` (game-engine) to set/extend the player's tier grant —
 * same-tier days stack, a higher tier upgrades to a fresh window, and a lower
 * tier over an active higher one is rejected up front.
 */
export const PREMIUM_BOOST_PRODUCTS = {
    "premium-7d": { id: "premium-7d", label: "Premium — 7 days", tier: "premium", days: 7, price: 200 },
    "premium-30d": { id: "premium-30d", label: "Premium — 30 days", tier: "premium", days: 30, price: 700 },
    "platinum-7d": { id: "platinum-7d", label: "Platinum — 7 days", tier: "platinum", days: 7, price: 400 },
    "platinum-30d": { id: "platinum-30d", label: "Platinum — 30 days", tier: "platinum", days: 30, price: 1400 },
};
/** Dark Matter cost of one Resource Pack purchase (`/purchaseResources`). */
export const RESOURCE_PURCHASE_PRICE = 50;
/** Maximum Resource Pack purchases allowed per planet per rolling 24h window. */
export const RESOURCE_PURCHASE_DAILY_LIMIT = 3;
/**
 * A Resource Pack grants `RESOURCE_PURCHASE_INCOME_HOURS` worth of the paying
 * planet's OWN hourly income (`resources.{metal,minerals,gas}.rate`) added
 * instantly to each of metal/minerals/gas, clamped to storage capacity — so
 * the payout scales with the planet's own economy rather than being a flat
 * number that trivializes early game / is irrelevant lategame.
 */
export const RESOURCE_PURCHASE_INCOME_HOURS = 10;
/**
 * Progressive (tax-bracket-style) Dark Matter pricing for instantly
 * completing a pending building/ship/ship-upgrade/recycle/science-research
 * queue item (`calculateRushCost` in `packages/game-engine/src/queueRush.ts`):
 * the first 60 minutes of remaining time cost 1 credit per 5 minutes, the
 * NEXT 180 minutes (i.e. up to 4h remaining) cost 0.5/5min, the NEXT 720
 * minutes (up to 16h remaining) cost 0.25/5min, and anything remaining beyond
 * that (16h+) costs {@link RUSH_PRICE_OVERFLOW_CREDITS_PER_FIVE_MINUTES}/5min.
 * `gravitationalManipulators` research can never be rushed regardless of
 * price — see `NON_RUSHABLE_SCIENCE_IDS` in `constants/sciences.ts`.
 */
export const RUSH_PRICE_TIERS = [
    { minutes: 60, creditsPerFiveMinutes: 1 },
    { minutes: 180, creditsPerFiveMinutes: 0.5 },
    { minutes: 720, creditsPerFiveMinutes: 0.25 },
];
/** Price for any remaining minutes beyond every bracket in {@link RUSH_PRICE_TIERS} (16h+ remaining). */
export const RUSH_PRICE_OVERFLOW_CREDITS_PER_FIVE_MINUTES = 0.1;
/**
 * Dark Matter cost per percentage point of a fleet's one-way travel-time
 * reduction, bought at dispatch via `SendFleetInput.speedBoostPercent`
 * (`FLEET_SPEED_BOOST_MAX_PERCENT` caps how much may be bought per fleet).
 */
export const FLEET_SPEED_BOOST_PRICE_PER_PERCENT = 1;
/** Maximum speed-boost percentage a single fleet dispatch may buy. */
export const FLEET_SPEED_BOOST_MAX_PERCENT = 50;
/**
 * Dark Matter paid to the alliance that wins a round, split in proportion to
 * each member's contribution points (`AllianceContribution`) among the
 * roster snapshotted at the membership freeze. Provisional, subject to
 * review. Anything left over by the per-player cap is not paid out.
 */
export const SUN_VICTORY_DARK_MATTER_POOL = 10000;
/** Most of {@link SUN_VICTORY_DARK_MATTER_POOL} any single player may receive. */
export const SUN_VICTORY_DARK_MATTER_PER_PLAYER_CAP = 1000;
/**
 * Dark Matter for the universe's top three players by ranking total at the
 * end of a round, first to third. Stacks with the alliance pool.
 */
export const ROUND_TOP_PLAYER_DARK_MATTER = [3000, 2000, 1000];
//# sourceMappingURL=premium.js.map